import React from 'react';
import { Mic, PhoneOff, Phone, MicOff, AlertCircle } from 'lucide-react';
import { useTwilioVoice } from './useTwilioVoice';
import './VoiceChatWidget.scss';
import AudioVisualizer from './AudioVisualizer';
import IdleAudioVisualizer from './IdleAudioVisualizer';

function applyColorStyle(color) {
  return color ? { backgroundColor: color } : {};
}

function applyTextColorStyle(color) {
  return color ? { color } : {};
}

export default function VoiceChatWidget({ voiceConfiguration }) {
  const {
    status,
    error,
    audioStream,
    isMuted,
    canConnect,
    canDisconnect,
    canToggleMute,
    handleConnect,
    handleDisconnect,
    handleToggleMute,
  } = useTwilioVoice(voiceConfiguration);

  const config = voiceConfiguration?.web_voice_widget_configuration || {};
  const theme = config.chat_audio_visualizer_theme || 'light';

  const getStatusStyle = () => {
    if (status === 'Error') {
      return applyTextColorStyle(
        voiceConfiguration?.web_voice_widget_configuration
          ?.chat_status_error_color
      );
    } else if (status === 'On Call') {
      return applyTextColorStyle(
        voiceConfiguration?.web_voice_widget_configuration
          ?.chat_status_on_call_color
      );
    } else if (status === 'Ready') {
      return applyTextColorStyle(
        voiceConfiguration?.web_voice_widget_configuration
          ?.chat_status_ready_color
      );
    } else if (status?.includes('Disconnecting')) {
      return applyTextColorStyle(
        voiceConfiguration?.web_voice_widget_configuration
          ?.chat_status_disconnect_color
      );
    } else {
      return applyTextColorStyle(
        voiceConfiguration?.web_voice_widget_configuration
          ?.chat_status_neutral_color
      );
    }
  };

  return (
    <div className={`prospera-widget-wrapper theme-${theme}`}>
      <div className="prospera-widget">
        <div className="prospera-status-block">
          {error && (
            <div className="prospera-alert">
              <AlertCircle className="prospera-alert-icon" />
              <div>
                <div className="prospera-alert-title">Error</div>
                <div className="prospera-alert-description">{error}</div>
              </div>
            </div>
          )}

          {status && (
            <div className="prospera-status" style={getStatusStyle()}>
              <span>Status: {status}</span>
              {[
                'Initializing...',
                'Fetching token...',
                'Initializing device...',
                'Connecting...',
              ].includes(status) && <div className="prospera-spinner" />}
            </div>
          )}
        </div>

        <div className="prospera-visualizer">
          {audioStream && !isMuted ? (
            <AudioVisualizer
              stream={audioStream}
              voiceConfiguration={voiceConfiguration}
              variant={
                voiceConfiguration?.web_voice_widget_configuration
                  ?.chat_audio_visualizer_variant
              }
              theme={
                voiceConfiguration?.web_voice_widget_configuration
                  ?.chat_audio_visualizer_theme
              }
              colors={{
                primary:
                  voiceConfiguration?.web_voice_widget_configuration
                    ?.chat_audio_visualizer_primary_color,
                secondary:
                  voiceConfiguration?.web_voice_widget_configuration
                    ?.chat_audio_visualizer_secondary_color,
                shadow:
                  voiceConfiguration?.web_voice_widget_configuration
                    ?.chat_audio_visualizer_shadow_color,
                gradientHue:
                  voiceConfiguration?.web_voice_widget_configuration
                    ?.chat_audio_visualizer_gradient_hue,
              }}
            />
          ) : (
            <IdleAudioVisualizer
              variant={
                voiceConfiguration?.web_voice_widget_configuration
                  ?.chat_audio_visualizer_variant
              }
              theme={
                voiceConfiguration?.web_voice_widget_configuration
                  ?.chat_audio_visualizer_theme
              }
            />
          )}
        </div>

        <div className="prospera-controls">
          <button
            onClick={handleConnect}
            className="prospera-btn"
            style={applyColorStyle(
              voiceConfiguration?.web_voice_widget_configuration
                ?.chat_connect_button_color
            )}
            disabled={!canConnect}
            title="Connect"
          >
            <Phone className="prospera-btn-icon" />
          </button>

          <button
            onClick={handleToggleMute}
            className="prospera-btn"
            style={applyColorStyle(
              voiceConfiguration?.web_voice_widget_configuration
                ?.chat_mute_button_color
            )}
            disabled={!canToggleMute}
            title={isMuted ? 'Unmute' : 'Mute'}
          >
            {isMuted ? (
              <MicOff className="prospera-btn-icon" />
            ) : (
              <Mic className="prospera-btn-icon" />
            )}
          </button>

          <button
            onClick={handleDisconnect}
            className="prospera-btn"
            style={applyColorStyle(
              voiceConfiguration?.web_voice_widget_configuration
                ?.chat_disconnect_button_color
            )}
            disabled={!canDisconnect}
            title="Disconnect"
          >
            <PhoneOff className="prospera-btn-icon" />
          </button>
        </div>
      </div>
    </div>
  );
}
